import store from 'state/RootReducer';
import { RestEnds } from '../Constants';
import { AuthActions } from 'state/authentication/Action';
import {
    AxiosStatic,
    AxiosResponseTransformer,
    CanceledError,
    AxiosError,
} from 'axios';

import { getTimeZoneDate } from 'shared/helpers/AgeOfDate';
import { Toast } from 'shared/helpers';

const axios: AxiosStatic = require('axios');

const dateResponseTransformer: AxiosResponseTransformer = (data) => {
    if (typeof data === 'string') {
        JSON.parse(data, (key, value) => {
            if (
                // eslint-disable-next-line max-len, no-useless-escape
                /\d{4}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[1-2]\d|3[0-1])T(?:[0-1]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d+|)(?:Z|(?:\+|\-)(?:\d{2}):?(?:\d{2}))/.test(
                    value
                )
            ) {
                const date = new Date(value);
                const dateZoned = getTimeZoneDate(date);

                // do your specific formatting here
                return dateZoned.toISOString();
            }
            return value;
        });
    }
    return data;
};

const AxiosManager = axios.create({
    baseURL: process.env.REACT_APP_BASE_URL,
    transformResponse: [
        dateResponseTransformer,
        ...(axios.defaults.transformResponse as AxiosResponseTransformer[]),
    ],
});

const getAccessToken = (): string => store.getState().authState.accessToken;
const getRefreshToken = (): string => store.getState().authState.refreshToken;

/**
 *  Used to modify the request headers common for all requests
 */
AxiosManager.interceptors.request.use((configInfo: any) => {
    const config = configInfo;
    // Append auth header
    if (
        config.headers['append-auth-header'] === 'true' &&
        getAccessToken() !== ''
    ) {
        config.headers['x-auth-token'] = `bearer ${getAccessToken()}`;
    }
    return config;
});

// To refresh accessToken and retry failed requests
let isAlreadyFetchingAccessToken = false;
let subscribers: any[] = [];
const onAccessTokenFetched = (accessToken: string): void => {
    subscribers = subscribers.filter((callback) => callback(accessToken));
};

const addSubscriber = (callback: any): void => {
    subscribers.push(callback);
};

const logout = (error: any): void => {
    console.log('------IdleCheck: AxiosManager file logout', error);
    if (error && error.response) {
        const { data, status } = error.response;
        console.log('------IdleCheck: Success Block', data);
        if (status === 400) {
            const { msg } = data;
            store.dispatch(
                AuthActions.doExpirityLogout({
                    isExpiritySession: true,
                    isIdleCheckLogout: true,
                    ExpritySessionMsg: msg,
                })
            );
        } else if (status === 401) {
            store.dispatch(
                AuthActions.doExpirityLogout({
                    isExpiritySession: true,
                    isIdleCheckLogout: true,
                    ExpritySessionMsg: 'Session token expired',
                })
            );
        } else {
            store.dispatch(
                AuthActions.doExpirityLogout({
                    isExpiritySession: true,
                    isIdleCheckLogout: true,
                    ExpritySessionMsg: 'Unable to refresh the token',
                })
            );
        }
    } else {
        const { data } = error;
        console.log('------IdleCheck: Else Block', data);
        store.dispatch(
            AuthActions.doExpirityLogout({
                isExpiritySession: true,
                isIdleCheckLogout: true,
                ExpritySessionMsg:
                    data && data.msg ? data.msg : 'failure to authenticate',
            })
        );
    }
};

// Function that will be called to refresh authorization
export const refreshAccessToken = (): any => {
    isAlreadyFetchingAccessToken = true;
    console.log('inside refreshAccessToken');
    return new Promise((resolve, reject) => {
        AxiosManager.post(
            RestEnds.REFRESH_TOKEN,
            {},
            {
                headers: {
                    'x-auth-token': `bearer ${getRefreshToken()}`,
                },
            }
        )
            .then((response: any) => {
                console.log(
                    '------IdleCheck: While fetch refresh token',
                    response
                );
                if (response instanceof AxiosError === false) {
                    const { accessToken, refreshToken } = response.data.data;
                    store.dispatch(
                        AuthActions.setLoginStatus({
                            accessToken,
                            refreshToken,
                        })
                    );
                    isAlreadyFetchingAccessToken = false;
                    onAccessTokenFetched(accessToken);
                    resolve('Token renewed');
                } else {
                    const { response: errorResponse } = response;
                    isAlreadyFetchingAccessToken = false;
                    subscribers = [];
                    console.log(
                        '------IdleCheck: Error while fetch refresh token',
                        errorResponse
                    );
                    logout(errorResponse);
                    reject(Error('Token renew failed'));
                }
            })
            .catch((error: any) => {
                console.log(
                    '------IdleCheck: Catch Error while fetch refresh token',
                    error
                );
                isAlreadyFetchingAccessToken = false;
                subscribers = [];
                logout(error);
            });
    });
};

AxiosManager.interceptors.request.use((response) => {
    const abortSignal = response.signal;
    response.validateStatus = (status) => {
        if (status === 429) {
            if (abortSignal) {
                Toast.showErrorMessage('Too Many Requests, Please try again!');
                abortSignal.dispatchEvent(new Event('abort'));
            }
        }
        return status >= 200 && status < 300;
    };

    return response;
});

AxiosManager.interceptors.response.use(
    (response: any) => response,
    (error: any) => {
        if (error instanceof CanceledError === false) {
            const {
                config,
                response: { status },
            } = error;
            const originalRequest = config;
            if (
                status === 401 &&
                config.url !== RestEnds.REFRESH_TOKEN &&
                config.url !== RestEnds.LOGOUT
            ) {
                if (!isAlreadyFetchingAccessToken) {
                    isAlreadyFetchingAccessToken = true;
                    return new Promise((resolve) => {
                        addSubscriber((accessToken: string) => {
                            originalRequest.headers[
                                'x-auth-token'
                            ] = `bearer ${accessToken}`;
                            resolve(AxiosManager(originalRequest));
                        });
                        refreshAccessToken();
                    });
                }
                // exclusion
                return new Promise((resolve) => {
                    addSubscriber((accessToken: string) => {
                        originalRequest.headers[
                            'x-auth-token'
                        ] = `bearer ${accessToken}`;
                        resolve(AxiosManager(originalRequest));
                    });
                });
            }
            return Promise.reject(error);
        }
        return Promise.reject(error);
    }
);

const { CancelToken: cancelation, Cancel } = axios;

export const CancelToken = cancelation;
export const CancelStatic = Cancel;

export default AxiosManager;
